import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { appState, AppStateValue, useSetState } from '../../../AppState';
import { getCachedUsers } from '../../../shared/cache/PlatformUserCache';
import { useTicketsPQ } from '../../../shared/hooks/useTicketsPQ';
import { graphService } from '../../../shared/services/graph.service';
import { platformService } from '../../../shared/services/platform.service';
import { getSideViewFilterIndex, setSideViewFilterIndex } from '../storageKeys';
import {
  buildSideViewListQuery,
  buildSideViewSelectedTicketQuery,
  filterSideViewTicketsForUpdatedTicket,
  isCustomViewDriven
} from '../ticketViewFilters';

interface UseSideViewTicketsArgs {
  api: platformService;
  ticketId: number | string;
  ticketStatus: any[] | undefined;
  isCustomViewEnabled: boolean;
  sortedViews:any[];
}

/**
 * Encapsulates the side-view ticket list: data fetching, dropdown filter
 * selection, and refreshing items after the active ticket is updated.
 */
export const useSideViewTickets = ({ api, ticketId, ticketStatus, isCustomViewEnabled, sortedViews }: UseSideViewTicketsArgs) => {
  const { getParentFiltersQuery } = useTicketsPQ();
  const currentState = appState();
  const graphAPI = new graphService();
  const setState = useSetState();
  const { t } = useTranslation();

  const [activeDropdownIndex, setActiveDropdownIndex] = useState<number>(getSideViewFilterIndex);
  const [sideViewTicketsState, setSideViewTicketsState] = useState<SideViewTicketState>({
    loading: false,
    hasMore: false,
    pageNumber: 1,
    tickets: [],
    totalTickets: 0,
    searchText: '',
    selectedViewId: 0
  });

  const handleSetActiveDropdownIndex = useCallback((index: number) => {
    setSideViewFilterIndex(index);
    setActiveDropdownIndex(index);
  }, []);

  const loadSideViewTickets = useCallback(
    async (page: number, query: string, _currentState:AppStateValue, _sortedViews:any[], _currentUser:any) => {
      setSideViewTicketsState(pre => ({ ...pre, loading: true }));
      
      try {
        const { data } = await api.getTicketList(await buildSideViewListQuery(page, query, _currentState, _sortedViews, api, t, graphAPI, setState, _currentUser));
        const requesters = await getCachedUsers(
          data.value.map((x: any) => x.RequesterId).filter((x: any) => x != null && x != undefined)
        );
        data.value.forEach((t: any) => {
          t.Requester = requesters.find((x: any) => x.Id == t.RequesterId);
        });

        let currentTickets: any[] = [];
        setSideViewTicketsState(pre => {
          currentTickets = [...pre.tickets];
          return { ...pre, tickets: currentTickets };
        });

        const selectedTicket = data.value.filter((x: any) => x.Id == ticketId);
        if (currentTickets.length <= 0) {
          if (selectedTicket.length <= 0) {
            const currentTicket = await api.getTicketList(
              buildSideViewSelectedTicketQuery(query ?? "", ticketId)
            );
            if ((currentTicket?.data?.value?.length ?? 0) > 0) {
              data.value.unshift(currentTicket.data.value[0]);
            }
          }
        } else if (selectedTicket.length > 0) {
          data.value.splice(
            data.value.findIndex((x: any) => x.Id == ticketId),
            1
          );
        }

        const users = await getCachedUsers(
          data.value
            .map((x: any) => [x.RequesterId, x.AssigneeId])
            .flat()
            .filter((x: any) => x != null && x != undefined)
        );
        data.value.forEach((t: any) => {
          t.Requester = users.find((x: any) => x.Id == t.RequesterId);
          t.Assignee = users.find((x: any) => x.Id == t.AssigneeId);
        });

        setSideViewTicketsState(pre => ({
          ...pre,
          loading: false,
          hasMore: data.value.length > 0,
          tickets: [...pre.tickets, ...data.value],
          totalTickets: data['@odata.count']
        }));
      } catch (error) {
        console.error(error);
        setSideViewTicketsState(pre => ({ ...pre, loading: false }));
      }
    },
    [api, ticketId]
  );

  const filterSideViewTicketsOnTicketSelection = useCallback(
    (data: any) => {
      setSideViewTicketsState(pre => ({
        ...pre,
        tickets: filterSideViewTicketsForUpdatedTicket(
          pre.tickets,
          ticketId,
          data,
          ticketStatus ?? [],
          activeDropdownIndex,
          // Read at call time, not captured: isCustomViewEnabled can still be false here while
          // the licence check is in flight. See isCustomViewDriven.
          isCustomViewDriven(isCustomViewEnabled, localStorage.getItem('selectedCustomViewId'))
        )
      }));
    },
    [ticketId, ticketStatus, activeDropdownIndex, isCustomViewEnabled]
  );

  useEffect(() => {
      if (sortedViews.length==0 || (currentState?.source?.length ?? 0) == 0 || !ticketStatus || ticketStatus.length <= 0) return;

      const filter = getSideViewFilterIndex();
      handleSetActiveDropdownIndex(filter);
      loadSideViewTickets(
        sideViewTicketsState.pageNumber,
        getParentFiltersQuery(isCustomViewEnabled, filter, sideViewTicketsState.searchText),
        currentState,
        sortedViews,
        currentState.me
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    
    
  }, [
    sideViewTicketsState.selectedViewId,
    sideViewTicketsState.pageNumber,
    sideViewTicketsState.searchText,
     ticketStatus,
     handleSetActiveDropdownIndex,
     activeDropdownIndex,
     sortedViews
  ]);

  return {
    activeDropdownIndex,
    setActiveDropdownIndex: handleSetActiveDropdownIndex,
    sideViewTicketsState,
    setSideViewTicketsState,
    filterSideViewTicketsOnTicketSelection,
    getParentFiltersQuery
  };
};
